import React, { useState } from 'react';
import { Calendar, Clock, ShoppingCart, X, Check, Eye, Sparkles } from 'lucide-react';
import { programs } from '../data/programs';
import CourseDetailModal from '../components/CourseDetailModal';

const ProgramPage = ({ selectedTrack, setSelectedTrack, onRegister, selectedPrograms, setSelectedPrograms }) => {
  const [showCart, setShowCart] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const filteredPrograms = selectedTrack === 'all'
    ? programs
    : programs.filter(program => program.track === selectedTrack);

  const toggleProgramSelection = (program) => {
    const isSelected = selectedPrograms.some(p => p.title === program.title && p.date === program.date);

    if (isSelected) {
      setSelectedPrograms(selectedPrograms.filter(p => !(p.title === program.title && p.date === program.date)));
    } else {
      setSelectedPrograms([...selectedPrograms, program]);
    }
  };

  const isProgramSelected = (program) => {
    return selectedPrograms.some(p => p.title === program.title && p.date === program.date);
  };

  const calculateTotal = () => {
    return selectedPrograms.reduce((total, program) => {
      const price = parseInt(program.price.replace(/[^0-9]/g, ''));
      return total + price;
    }, 0);
  };

  const formatRupiah = (number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(number);
  };

  const handleRegisterMultiple = () => {
    // Create a combined program object for multiple selections
    const combinedProgram = {
      title: `Paket Pilihan (${selectedPrograms.length} Kelas)`,
      track: 'Custom Bundle',
      price: formatRupiah(calculateTotal()),
      programs: selectedPrograms
    };
    onRegister(combinedProgram);
    setShowCart(false);
  };

  const handleViewDetail = (program) => {
    setSelectedCourse(program);
    setShowDetailModal(true);
  };

  const trackCategories = [
    { id: 'all', label: 'Semua Kelas', icon: '', color: 'brown' },
    { id: 'Data Analyst', label: 'Data Analyst', icon: '', color: 'brown' },
    { id: 'Data Science', label: 'Data Science', icon: '', color: 'brown' },
    { id: 'Google Workspace', label: 'Google Workspace', icon: '', color: 'brown' },
    { id: 'Bundling', label: 'Paket Hemat', icon: '', color: 'cream' }
  ];

  return (
    <div className="bg-blue-50 min-h-screen transition-colors">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-blue-700 via-blue-600 to-blue-700 py-16 relative overflow-hidden transition-colors">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-blue-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-300 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-block bg-blue-200 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <Sparkles className="w-4 h-4 inline mr-2" />
            Program Pembelajaran Interaktif
          </div>
          <h1 className="text-3xl md:text-5xl font-display font-bold text-blue-50 mb-4 leading-tight">
            Mulai Karir Impian di Bidang Data
          </h1>
          <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
            Pilih kelas sesuai kebutuhan, atau ambil paket hemat untuk pembelajaran lebih lengkap
          </p>
        </div>
      </section>

      {/* Programs List */}
      <section className="py-12 bg-blue-50 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-4 text-blue-900">
            Pilih Kelas yang Cocok untuk Anda
          </h2>

          {/* Multi-select Info */}
          <div className="bg-gradient-to-r from-blue-100 to-cream-200 border-2 border-blue-300 rounded-xl p-4 mb-8 max-w-3xl mx-auto">
            <div className="flex items-center gap-3 text-blue-800">
              <div className="bg-white p-2 rounded-lg border-2 border-blue-400 shadow-md">
                <Check className="w-5 h-5 text-blue-700" strokeWidth={3} />
              </div>
              <p className="text-sm md:text-base font-semibold">
                Klik kotak di pojok kanan atas setiap kelas untuk memilih beberapa kelas sekaligus dan lihat total harganya
              </p>
            </div>
          </div>

          {/* Track Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {trackCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedTrack(category.id)}
                className={`px-5 py-3 rounded-xl text-base font-semibold transition-all shadow-sm ${
                  selectedTrack === category.id
                    ? category.color === 'cream'
                      ? 'bg-gradient-to-r from-cream-400 to-cream-500 text-blue-900 shadow-lg scale-105'
                      : 'bg-blue-700 text-blue-50 shadow-lg scale-105'
                    : 'bg-white text-blue-800 hover:bg-blue-50 border-2 border-blue-200'
                }`}
              >
                <span className="mr-2">{category.icon}</span>
                {category.label}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPrograms.map((program, index) => {
              const isSelected = isProgramSelected(program);
              return (
              <div
                key={index}
                className={`bg-white border-3 rounded-2xl p-6 hover:shadow-2xl transition-all relative group ${
                  isSelected ? 'border-blue-600 shadow-xl ring-4 ring-brown-200' : 'border-blue-200 hover:border-blue-400'
                }`}
              >
                {/* Checkbox - Highly Visible */}
                <div
                  className="absolute top-3 right-3 cursor-pointer z-20"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleProgramSelection(program);
                  }}
                  title="Pilih kelas ini"
                >
                  <div className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-all shadow-lg ${
                    isSelected
                      ? 'bg-blue-700 border-blue-800 scale-110'
                      : 'bg-white border-blue-400 hover:border-blue-600 hover:shadow-xl'
                  }`}>
                    {isSelected && <Check className="w-6 h-6 text-blue-50" strokeWidth={3} />}
                  </div>
                </div>

                {/* Badges */}
                <div className="mb-4 flex flex-wrap gap-2 pr-10">
                  <span className="bg-blue-100 text-blue-800 text-xs px-3 py-1.5 rounded-full font-semibold">
                    📂 {program.track}
                  </span>
                  {program.prerequisite && (
                    <span className="bg-blue-400 text-blue-900 text-xs px-3 py-1.5 rounded-full font-semibold">
                      ⚠️ Advanced
                    </span>
                  )}
                  {program.popular && (
                    <span className="bg-gradient-to-r from-cream-400 to-cream-500 text-blue-900 text-xs px-3 py-1.5 rounded-full font-bold">
                      ⭐ Paling Diminati
                    </span>
                  )}
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold mb-2 text-blue-900 leading-tight">{program.title}</h3>
                <p className="text-sm text-blue-600 font-semibold mb-3">{program.module}</p>
                <p className="text-blue-700 text-sm mb-4 line-clamp-2">{program.material}</p>

                {/* Info */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-blue-600">
                    <Calendar className="w-4 h-4 mr-2 text-blue-500" />
                    {program.date}
                  </div>
                  <div className="flex items-center text-sm text-blue-600">
                    <Clock className="w-4 h-4 mr-2 text-blue-500" />
                    {program.duration}
                  </div>
                </div>

                {/* Footer */}
                <div className="pt-4 border-t-2 border-blue-100 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-blue-900">{program.price}</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewDetail(program)}
                      className="flex-1 bg-blue-100 text-blue-800 px-4 py-2.5 rounded-xl hover:bg-blue-200 transition-all font-semibold text-sm flex items-center justify-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      Lihat Detail
                    </button>
                    <button
                      onClick={() => onRegister(program)}
                      className="flex-1 bg-blue-700 text-blue-50 px-4 py-2.5 rounded-xl hover:bg-blue-800 transition-all font-bold text-sm shadow-md hover:shadow-lg"
                    >
                      Daftar
                    </button>
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        </div>
      </section>

      {/* Floating Cart Button */}
      {selectedPrograms.length > 0 && (
        <div className="fixed bottom-6 right-6 z-40 animate-bounce">
          <button
            onClick={() => setShowCart(true)}
            className="bg-gradient-to-r from-blue-700 to-blue-600 text-blue-50 px-6 py-4 rounded-2xl shadow-2xl hover:shadow-3xl transition-all flex items-center gap-3 border-2 border-blue-800 hover:scale-105"
          >
            <div className="bg-blue-100 p-2 rounded-lg">
              <ShoppingCart className="w-6 h-6 text-blue-800" />
            </div>
            <div className="text-left">
              <div className="text-sm font-semibold text-blue-200">{selectedPrograms.length} Kelas Dipilih</div>
              <div className="text-xl font-bold">{formatRupiah(calculateTotal())}</div>
            </div>
          </button>
        </div>
      )}

      {/* Cart Modal */}
      {showCart && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
          <div className="bg-blue-50 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl border-2 border-blue-200">
            {/* Header */}
            <div className="p-6 border-b-2 border-blue-200 bg-gradient-to-r from-blue-700 to-blue-600 flex items-center justify-between">
              <h2 className="text-2xl font-display font-bold text-blue-50">🛒 Keranjang Kelas</h2>
              <button
                onClick={() => setShowCart(false)}
                className="bg-blue-800 hover:bg-blue-900 text-blue-50 rounded-full p-2 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-6 bg-blue-50">
              <div className="space-y-3">
                {selectedPrograms.map((program, index) => (
                  <div key={index} className="bg-white border-2 border-blue-200 rounded-xl p-4 flex items-start gap-4 hover:shadow-md transition">
                    <button
                      onClick={() => toggleProgramSelection(program)}
                      className="bg-red-100 hover:bg-red-200 text-red-600 rounded-lg p-2 mt-1 transition"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="flex-1">
                      <h3 className="font-bold text-blue-900 text-base mb-2">{program.title}</h3>
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-semibold">
                          📂 {program.track}
                        </span>
                      </div>
                      <div className="text-sm text-blue-600 space-y-1">
                        <div className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1.5 text-blue-500" />
                          {program.date}
                        </div>
                        <div className="flex items-center">
                          <Clock className="w-3 h-3 mr-1.5 text-blue-500" />
                          {program.duration}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-blue-900 text-lg">{program.price}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t-2 border-blue-200 bg-white">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-3">
                <div>
                  <div className="text-sm text-blue-600 font-medium">Total ({selectedPrograms.length} kelas)</div>
                  <div className="text-3xl md:text-4xl font-bold text-blue-900">{formatRupiah(calculateTotal())}</div>
                </div>
                <button
                  onClick={handleRegisterMultiple}
                  className="w-full sm:w-auto bg-blue-700 text-blue-50 px-8 py-4 rounded-xl hover:bg-blue-800 transition-all font-bold text-lg shadow-lg hover:shadow-xl"
                >
                  Daftar Semua Sekarang
                </button>
              </div>
              <p className="text-sm text-blue-600 text-center bg-blue-100 rounded-lg py-2 px-4">
                💡 Hemat waktu dengan mendaftar semua kelas sekaligus!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Course Detail Modal */}
      <CourseDetailModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        course={selectedCourse}
        onRegister={onRegister}
      />
    </div>
  );
};

export default ProgramPage;
